import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import usePortal from 'react-cool-portal';
import { Box, ThemeUICSSObject, useThemeUI } from '@apia/theme';
import { isChild } from '../../../../utils/dom';
import { AutocompleteContext } from './AutocompleteContext';

export default function useAutocompleteBox(
  getAttachComponent: () => HTMLElement,
) {
  const boxRef = useRef<HTMLElement | null>(null);
  const { Portal } = usePortal({
    containerId: 'NonControlledAppModals',
    autoRemoveContainer: false,
    defaultShow: true,
    clickOutsideToHide: false,
    escToHide: false,
  });

  const initialIsOpen = useRef<boolean>(false);
  const setIsOpen = useRef<Dispatch<SetStateAction<boolean>>>(
    (isOpen: SetStateAction<boolean>) => {
      if (isOpen instanceof Function)
        initialIsOpen.current = isOpen(initialIsOpen.current);
      else initialIsOpen.current = isOpen;
    },
  );

  const initialSx = useRef<ThemeUICSSObject>({});
  const setSx = useRef<Dispatch<SetStateAction<ThemeUICSSObject>>>(
    (sx: SetStateAction<ThemeUICSSObject>) => {
      if (sx instanceof Function) initialSx.current = sx(initialSx.current);
      else initialSx.current = sx;
    },
  );

  useEffect(() => {
    function closeBox() {
      setIsOpen.current(false);
    }
    function handleClick(ev: MouseEvent) {
      const target = ev.target as HTMLElement;
      if (
        target.classList.contains('autocomplete__optionsContainer') ||
        target.classList.contains('optionsBox')
      ) {
        return;
      }

      if (!isChild(target, (current) => current === getAttachComponent())) {
        closeBox();
      }
    }
    function handleEscape(ev: KeyboardEvent) {
      if (ev.code === 'Escape') closeBox();
    }

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', closeBox);
    document.addEventListener('rezise', closeBox);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', closeBox);
      document.removeEventListener('rezise', closeBox);
    };
  }, [getAttachComponent]);

  const apiaTheme = useThemeUI();

  const useIsOpenSetter = useRef<Dispatch<SetStateAction<boolean>> | null>(
    null,
  );

  return {
    isOpen: () => {
      return initialIsOpen.current;
    },
    useIsOpen: useCallback(() => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [isOpen, setIsOpen] = useState(initialIsOpen.current);

      useIsOpenSetter.current = setIsOpen;

      return isOpen;
    }, []),
    OptionsBox: useCallback(
      ({ children }: { children: ReactNode }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [isOpen, setIsOpenState] = useState(initialIsOpen.current);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [sx, setSxState] = useState<ThemeUICSSObject>(initialSx.current);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { disabled, readonly } = useContext(AutocompleteContext);

        setSx.current = setSxState;
        setIsOpen.current = setIsOpenState;
        initialIsOpen.current = isOpen;

        return (
          <Portal>
            <Box
              ref={boxRef}
              className={`optionsBox ${disabled ? 'disabled' : ''} ${
                readonly ? 'readonly' : ''
              }`}
              sx={{
                ...sx,
                display: isOpen ? 'block' : 'none',
                '&.disabled .autocomplete__optionsContainer': {
                  ...apiaTheme.theme.palette.getColorsByPath(
                    'form.fields.disabled',
                  ),
                },
                '&.readonly .autocomplete__optionsContainer': {
                  ...apiaTheme.theme.palette.getColorsByPath(
                    'form.fields.readonly',
                  ),
                },
              }}
            >
              {isOpen ? children : null}
            </Box>
          </Portal>
        );
      },
      [Portal, apiaTheme.theme.palette],
    ),
    toggle: useCallback(
      (shouldOpen?: boolean) => {
        let result = false;

        setIsOpen.current((isCurrentlyOpen) => {
          if (
            shouldOpen === false ||
            (shouldOpen === undefined && isCurrentlyOpen)
          )
            return false;

          const element = getAttachComponent();

          if (!element) {
            console.error('Cannot get component');
          }

          const { height, left, width, top } = element.getBoundingClientRect();
          const bottom = top + height;

          const showBottom =
            window.innerHeight - bottom > 150 ||
            top < window.innerHeight - bottom;
          const maxHeight = showBottom ? window.innerHeight - bottom : top;

          const commonStyles: ThemeUICSSObject = {
            left,
            maxHeight,
            overflow: 'auto',
            position: 'fixed',
            zIndex: 'contextMenu',
          };

          setSx.current(
            showBottom
              ? {
                  ...commonStyles,
                  width,
                  top: `${bottom}px`,
                }
              : {
                  ...commonStyles,
                  bottom: `${window.innerHeight - top}px`,
                  width,
                },
          );

          result = true;

          return true;
        });
        useIsOpenSetter.current?.(result);
      },
      [getAttachComponent],
    ),
  };
}
